import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  User as UserIcon,
  Mail,
  Phone,
  ShieldCheck,
  Activity,
  Calendar,
  FileText,
  LogIn,
  UserPlus,
  LogOut,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const EVENT_META = {
  signup_started: { label: "Signup started", icon: UserPlus, tone: "muted" },
  signup_completed: { label: "Signup completed", icon: UserPlus, tone: "ok" },
  login: { label: "Signed in", icon: LogIn, tone: "yellow" },
  logout: { label: "Signed out", icon: LogOut, tone: "muted" },
  pdf_rewrite: { label: "PDF rewrite", icon: FileText, tone: "yellow" },
};

const toneClasses = {
  muted: "text-neutral-500",
  ok: "text-[#4ADE80]",
  yellow: "text-[#E2FF54]",
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (_) {
    return ts;
  }
}

export default function Portal() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/portal/activities?limit=200");
      setActivities(data.activities || []);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = activities.reduce((acc, a) => {
    acc[a.event] = (acc[a.event] || 0) + 1;
    return acc;
  }, {});
  const rewrites = counts.pdf_rewrite || 0;
  const logins = counts.login || 0;

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14 border-b border-neutral-900">
        <p className="mono text-[11px] uppercase tracking-[0.32em] text-[#E2FF54]">
          // my client portal
        </p>
        <h1 className="display text-4xl md:text-6xl font-bold uppercase mt-3 leading-[0.95]">
          Welcome back,
          <br />
          <span className="text-neutral-500">{user?.name || user?.email}</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-neutral-900">
        {/* Profile */}
        <div className="lg:col-span-4 lg:border-r border-neutral-900 p-6 md:p-10 space-y-6">
          <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
            [ profile ]
          </h2>
          <ProfileField icon={UserIcon} label="name" value={user?.name || "—"} />
          <ProfileField icon={Mail} label="email" value={user?.email} />
          <ProfileField icon={Phone} label="phone" value={user?.phone || "—"} />
          <ProfileField
            icon={ShieldCheck}
            label="role"
            value={user?.role}
            valueClass={user?.role === "admin" ? "text-[#E2FF54]" : "text-neutral-300"}
          />
          <ProfileField
            icon={Calendar}
            label="member since"
            value={fmt(user?.created_at)}
          />
          <div className="pt-4 border-t border-neutral-900 grid grid-cols-2 gap-3">
            <Stat label="rewrites" value={rewrites} />
            <Stat label="sessions" value={logins} />
          </div>
        </div>

        {/* Activity */}
        <div className="lg:col-span-8 p-6 md:p-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ activity_log ]
            </h2>
            <button
              onClick={load}
              className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white flex items-center gap-1"
              data-testid="refresh-activities"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> refresh
            </button>
          </div>

          {error && (
            <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-3 mono text-xs text-[#FF9994] mb-4">
              {error}
            </div>
          )}

          <div className="border border-neutral-900" data-testid="activities-table">
            <div className="grid grid-cols-12 border-b border-neutral-800 bg-[#0a0a0a]">
              <div className="col-span-4 md:col-span-3 border-r border-neutral-900 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                event
              </div>
              <div className="col-span-5 md:col-span-6 border-r border-neutral-900 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                details
              </div>
              <div className="col-span-3 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                when
              </div>
            </div>
            {loading ? (
              <SkeletonRows />
            ) : activities.length === 0 ? (
              <div className="p-10 mono text-xs text-neutral-500 text-center flex flex-col items-center gap-2">
                <Activity size={22} className="text-neutral-700" />
                no activity yet — try rewriting a PDF
              </div>
            ) : (
              activities.map((a, idx) => (
                <ActivityRow key={a.id} activity={a} idx={idx} />
              ))
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ProfileField({ icon: Icon, label, value, valueClass = "text-neutral-300" }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-600 flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className={cn("mono text-sm mt-1 truncate", valueClass)}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-neutral-900 p-3">
      <div className="mono text-[9px] uppercase tracking-[0.22em] text-neutral-600">
        {label}
      </div>
      <div className="mono text-2xl text-[#E2FF54] mt-1">{value}</div>
    </div>
  );
}

function ActivityRow({ activity, idx }) {
  const meta = EVENT_META[activity.event] || {
    label: activity.event,
    icon: Activity,
    tone: "muted",
  };
  const Icon = meta.icon;
  const details = activity.metadata || {};
  const detailText =
    activity.event === "pdf_rewrite"
      ? `${details.filename || "file"} · ${details.pages ?? "?"}p · ${
          details.size ? (details.size / 1024).toFixed(1) + " KB" : ""
        }`
      : JSON.stringify(details) === "{}"
      ? "—"
      : Object.entries(details)
          .map(([k, v]) => `${k}: ${v}`)
          .join(" · ");

  return (
    <div
      className="row-anim grid grid-cols-12 border-b border-neutral-900 hover:bg-neutral-900/40"
      style={{ animationDelay: `${idx * 20}ms` }}
      data-testid={`activity-row-${activity.id}`}
    >
      <div
        className={cn(
          "col-span-4 md:col-span-3 border-r border-neutral-900 px-4 py-3 mono text-xs uppercase tracking-[0.15em] flex items-center gap-2",
          toneClasses[meta.tone]
        )}
      >
        <Icon size={12} /> {meta.label}
      </div>
      <div className="col-span-5 md:col-span-6 border-r border-neutral-900 px-4 py-3 mono text-xs text-neutral-400 truncate">
        {detailText}
      </div>
      <div className="col-span-3 px-4 py-3 mono text-[11px] text-neutral-500">
        {fmt(activity.timestamp)}
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="grid grid-cols-12 border-b border-neutral-900">
          <div className="col-span-4 md:col-span-3 border-r border-neutral-900 px-4 py-4">
            <div className="h-3 bg-neutral-900 animate-pulse w-24" />
          </div>
          <div className="col-span-5 md:col-span-6 border-r border-neutral-900 px-4 py-4">
            <div className="h-3 bg-neutral-900 animate-pulse w-3/4" />
          </div>
          <div className="col-span-3 px-4 py-4">
            <div className="h-3 bg-neutral-900 animate-pulse w-20" />
          </div>
        </div>
      ))}
    </>
  );
}
